import FocusTrap from "focus-trap-react";
import { useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import useReferralDashboard from "../../../hooks/useReferralDashboard";
import intl from "../../../services/intl";
import userSelectors from "../../../store/user/selectors";
import useVariation from "../../../hooks/useVariation";
import metrics from "../../../utils/metrics";
import { Icons } from "../../../utils/react-svg";
import { Color, responsive } from "../../../utils/style";
import MagicLink from "../../MagicLink";
import {
  NavDashboard,
  DashboardMenuItem,
  UserTile,
  UserAvatar,
} from "@ritual/essentials-for-react";

const IconContainer = styled.div`
  position: relative;
`;

const Dropdown = styled.div`
  display: none;

  width: 307px;
  padding: 32px;
  background-color: ${Color.white};
  box-shadow: 0px 8px 30px -10px rgba(20, 43, 111, 0.16);

  position: absolute;
  right: -64px;
  top: 37px;

  ${responsive.md`
    right: -61px;
    top: 45px;
  `}

  &.expanded {
    display: flex;
    flex-direction: column;
  }
`;

const LinkList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const LinkItem = styled.li`
  font-weight: 300;
  margin-bottom: 12px;

  &:last-of-type {
    margin-bottom: 0;
  }

  &:hover {
    opacity: 0.56;
  }
`;

const Button = styled.button`
  padding: 0;
  border: 0;
  background: none;

  display: flex;
  align-items: center;
`;

const AccountIcon = styled(Icons.Account)`
  height: 20px;
  width: 21px;

  ${responsive.md`
    height: 24px;
    width: 25px;
  `}
`;

const AccountInitialsIcon = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 20px;
  width: 21px;
  border-radius: 32px;
  border: 2px solid var(--yellow-ochre-80, #ffde33);
  background: var(--yellow-ochre-40, #ffef99);
  color: var(--indigo-blue);
  font-weight: 500;
  font-size: 10px;

  ${responsive.md`
    height: 24px;
    width: 25px;
  `}
`;

const NavDashboardDropdown = styled(Dropdown)`
  padding: unset;
`;

const UserName = styled.h3`
  margin-bottom: unset;
`;

const UserContact = styled.h4`
  margin-bottom: unset;
`;

const AccountUrl = process.env.GATSBY_ACCOUNT_URL;

const AccountDropdown = ({ onClick }) => {
  const [expanded, setExpanded] = useState(false);
  const referralDashboard = useReferralDashboard();
  const isLoggedIn = useSelector(userSelectors.isLoggedIn);
  const activeUser = useSelector(userSelectors.activeUser);
  const { firstName, lastName, email } = activeUser || {};
  const firstInitial = firstName?.charAt(0).toUpperCase();
  const fullName = `${firstName || ""} ${lastName || ""}`.trim();

  const accountRedesignFlag = useVariation("account-redesign");
  const accountRedesignEnabled =
    accountRedesignFlag && accountRedesignFlag !== "control";

  const Links = [
    { title: "nav.account.home", href: `${AccountUrl}` },
    { title: "nav.account.profile", href: `${AccountUrl}/me/update` },
    { title: "nav.account.order-history", href: `${AccountUrl}/order-history` },
    ...(referralDashboard.enabled
      ? [{ title: "nav.account.refer", href: `${AccountUrl}/refer` }]
      : []),
    { title: "nav.account.sign-out", href: `${AccountUrl}/logout` },
  ];

  function handleClick() {
    if (isLoggedIn) {
      setExpanded(!expanded);
      const eventName = !expanded ? "Nav Opened" : "Nav Closed";
      metrics.track(eventName, {
        location: "Header Nav",
      });
    }
    onClick && onClick();
  }

  function hideDropdown() {
    setExpanded(false);
  }

  function onLinkClick(e, link = undefined) {
    const { href, innerHTML } = e.target;
    e.preventDefault();

    metrics.track("Nav Item Clicked", {
      location: "Header Nav",
      title: innerHTML,
    });

    window.location = link?.href ?? href;
  }

  const avatarIcon =
    accountRedesignEnabled && isLoggedIn ? (
      <AccountInitialsIcon>{firstInitial}</AccountInitialsIcon>
    ) : (
      <AccountIcon />
    );

  if (!isLoggedIn) {
    return (
      <IconContainer>
        <MagicLink
          aria-label={intl.t("nav.account.label")}
          to="/login"
          onClick={handleClick}
        >
          <AccountIcon />
        </MagicLink>
      </IconContainer>
    );
  }

  return (
    <FocusTrap
      active={expanded}
      focusTrapOptions={{
        clickOutsideDeactivates: false,
        onDeactivate: hideDropdown,
      }}
    >
      <IconContainer>
        <Button
          aria-label={intl.t("nav.account.label")}
          aria-haspopup="true"
          aria-expanded={expanded}
          onClick={handleClick}
        >
          {avatarIcon}
        </Button>
        {accountRedesignEnabled ? (
          <NavDashboardDropdown className={expanded && "expanded"}>
            <NavDashboard>
              {/* istanbul ignore next */}
              <UserTile slot="nav-dashboard-header" handleClick={(e) => onLinkClick(e, Links[0])}>
                <UserAvatar slot="user-tile_avatar">
                  <span slot="user-avater-content">{firstInitial}</span>
                </UserAvatar>
                <UserName slot="user-tile-name">{fullName}</UserName>
                <UserContact slot="user-tile-contact">{email}</UserContact>
              </UserTile>
              {Links.map((link) => (
                <DashboardMenuItem
                  slot="nav-dashboard-item"
                >
                  <a
                    href={link.href}
                    onClick={onLinkClick}
                  >
                    {intl.t(link.title)}
                  </a>
                </DashboardMenuItem>
              ))}
            </NavDashboard>
          </NavDashboardDropdown>
        ) : (
          <Dropdown className={expanded && "expanded"}>
            <LinkList>
              {Links.map((link) => (
                <LinkItem key={link.title}>
                  <a href={link.href} onClick={onLinkClick}>
                    {intl.t(link.title)}
                  </a>
                </LinkItem>
              ))}
            </LinkList>
          </Dropdown>
        )}
      </IconContainer>
    </FocusTrap>
  );
};

export default AccountDropdown;
